"use client";

import { c as _c } from "react/compiler-runtime";
import type { ComponentType, SVGProps } from "react";
import { Globe, Music2 } from "lucide-react";
import type { CampaignSectionStyle } from "@growwithai/shared";
import { sectionBgStyle, sectionHeadingStyle } from "../section-style";

// Tipe lokal (bukan Extract dari shared) — kontrak section 'socials' bisa mendarat
// terpisah di @growwithai/shared; komponen ini defensif terhadap absensinya.
export interface SocialsSectionData {
  id: string;
  type: "socials";
  hidden?: boolean;
  title?: string;
  instagram?: string;
  tiktok?: string;
  youtube?: string;
  x?: string;
  facebook?: string;
  website?: string;
  custom_style?: CampaignSectionStyle;
}
type IconProps = SVGProps<SVGSVGElement>;

// Ikon brand digambar inline gaya lucide (stroke currentColor) — versi
// lucide-react terpasang sudah tidak menyediakan ikon brand.
const svgBase = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round"
} as const;
function InstagramIcon(props) {
  const $ = _c(5);
  let t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <rect x="2" y="2" width="20" height="20" rx="5" />;
    t1 = <path d="M16 11.37a4 4 0 1 1-7.914 1.174A4 4 0 0 1 16 11.37z" />;
    t2 = <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== props) {
    t3 = <svg {...svgBase} {...props}>{t0}{t1}{t2}</svg>;
    $[3] = props;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function YoutubeIcon(props) {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" />;
    t1 = <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] !== props) {
    t2 = <svg {...svgBase} {...props}>{t0}{t1}</svg>;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function XIcon(props) {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path d="M4 4l16 16" />;
    t1 = <path d="M20 4L4 20" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] !== props) {
    t2 = <svg {...svgBase} {...props}>{t0}{t1}</svg>;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function FacebookIcon(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <svg {...svgBase} {...props}>{t0}</svg>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
const PLATFORMS: {
  key: keyof SocialsSectionData;
  label: string;
  icon: ComponentType<IconProps>;
}[] = [{
  key: "instagram",
  label: "Instagram",
  icon: InstagramIcon
}, {
  key: "tiktok",
  label: "TikTok",
  icon: Music2
}, {
  key: "youtube",
  label: "YouTube",
  icon: YoutubeIcon
}, {
  key: "x",
  label: "X (Twitter)",
  icon: XIcon
}, {
  key: "facebook",
  label: "Facebook",
  icon: FacebookIcon
}, {
  key: "website",
  label: "Website",
  icon: Globe
}];

/** Baris ikon sosial media: tombol bundar ≥48px, aria-label per platform. */
export default function SocialsSection(t0) {
  const $ = _c(24);
  const {
    section
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== section) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const links = PLATFORMS.map(p => ({
        ...p,
        url: typeof section[p.key] === "string" ? (section[p.key] as string).trim() : ""
      })).filter(_temp);
      if (links.length === 0) {
        t7 = null;
        break bb0;
      }
      t5 = "bg-warm-cream py-12 md:py-16";
      if ($[8] !== section.custom_style) {
        t6 = sectionBgStyle(section.custom_style);
        $[8] = section.custom_style;
        $[9] = t6;
      } else {
        t6 = $[9];
      }
      t3 = "mx-auto max-w-3xl px-5 text-center";
      if ($[10] !== section.custom_style || $[11] !== section.title) {
        t4 = section.title && <h2 className="mb-6 text-2xl font-bold tracking-tight text-warm-black md:text-3xl" style={sectionHeadingStyle(section.custom_style)}>{section.title}</h2>;
        $[10] = section.custom_style;
        $[11] = section.title;
        $[12] = t4;
      } else {
        t4 = $[12];
      }
      t1 = "flex flex-wrap items-center justify-center gap-3";
      t2 = links.map(_temp2);
    }
    $[0] = section;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
    t7 = $[7];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[13] !== t1 || $[14] !== t2) {
    t8 = <div className={t1}>{t2}</div>;
    $[13] = t1;
    $[14] = t2;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t3 || $[17] !== t4 || $[18] !== t8) {
    t9 = <div className={t3}>{t4}{t8}</div>;
    $[16] = t3;
    $[17] = t4;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t5 || $[21] !== t6 || $[22] !== t9) {
    t10 = <section className={t5} style={t6}>{t9}</section>;
    $[20] = t5;
    $[21] = t6;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}
function _temp2(t0) {
  const {
    key,
    label,
    icon: Icon,
    url
  } = t0;
  return <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={label} title={label} className="flex h-12 w-12 items-center justify-center rounded-full border border-black/[0.08] bg-white text-[var(--brand-primary)] shadow-sm transition-all hover:border-[var(--brand-primary)] hover:bg-[color-mix(in_srgb,var(--brand-primary)_8%,white)] active:scale-95"><Icon className="h-5 w-5" aria-hidden={true} /></a>;
}
function _temp(p_0) {
  return /^https?:\/\//i.test(p_0.url);
}